import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Users, Calendar, CheckCircle, XCircle, Pencil, CalendarDays,
  Download, Clock, Award, ArrowLeftRight, FileSpreadsheet, UtensilsCrossed, ChevronLeft, ChevronRight, Trash2, UserPlus,
} from 'lucide-react';
import { employesApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { allowedRhTabs } from '../../utils/permissions';
import { useLocation } from 'react-router-dom';
import { getStatutBadgeClass, getStatutLabel } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmployeForm from './EmployeForm';
import CongesForm from './CongesForm';
import PlanningPage from './PlanningPage';
import RefacturationPage from './RefacturationPage';
import RecrutementPage from './RecrutementPage';

function EmployeCard({ employe, onEdit, onHeuresSupp, structures = [], canTransfer = false, canDelete = false, onTransfer = () => {}, onDelete = () => {} }) {
  const [hsInput, setHsInput] = useState('');
  const [showHsForm, setShowHsForm] = useState(false);

  return (
    <div className="card p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-semibold text-gray-900">{employe.prenom} {employe.nom}</div>
          <div className="text-sm text-gray-500">{employe.poste}</div>
          <div className="text-xs text-gray-400">{employe.type_contrat}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(employe)} title="Modifier" className="p-1.5 rounded hover:bg-gray-100 text-gray-400">
            <Pencil size={14} />
          </button>
          {canDelete && (
            <button onClick={() => onDelete(employe)} title="Supprimer définitivement" className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {canTransfer && structures.length > 1 ? (
        <div className="flex items-center gap-1.5 mb-2">
          <ArrowLeftRight size={12} className="text-gray-400 flex-shrink-0" />
          <select
            value={employe.structure_id || ''}
            onChange={e => onTransfer(employe, e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-1.5 py-1 bg-white text-gray-600 w-full"
            title="Transférer vers une autre structure"
          >
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
      ) : (
        <div className="text-xs text-gray-400 mb-2">{employe.structure_nom}</div>
      )}

      {/* Ancienneté */}
      {employe.date_embauche && (
        <div className="flex items-center gap-1.5 mb-3 text-xs">
          <Award size={12} className="text-amber-500" />
          <span className="text-gray-500">
            {employe.anciennete_annees} an{employe.anciennete_annees > 1 ? 's' : ''} d'ancienneté
          </span>
          {employe.eligible_cp_anciennete && (
            <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-medium">
              +5j CP anc.
            </span>
          )}
        </div>
      )}

      {/* Soldes congés */}
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        <div className="bg-green-50 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-green-600">{employe.solde_cp}</div>
          <div className="text-[10px] text-green-500">CP légaux</div>
        </div>
        {employe.eligible_cp_anciennete && (
          <div className="bg-amber-50 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-amber-600">{employe.solde_cp_anciennete || 0}</div>
            <div className="text-[10px] text-amber-500">CP anc.</div>
          </div>
        )}
      </div>

      {/* Heures supp */}
      <div className="bg-navy-50 rounded-lg px-3 py-2 flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Clock size={13} className="text-navy-500" />
          <span className="text-xs text-navy-500">H. supp :</span>
          <span className={`text-sm font-bold ${(employe.heures_supp || 0) > 0 ? 'text-navy-700' : 'text-navy-300'}`}>
            {(employe.heures_supp || 0).toFixed(1)}h
          </span>
        </div>
        <button onClick={() => setShowHsForm(!showHsForm)}
          className="text-navy-300 hover:text-navy-700 text-xs underline">
          {showHsForm ? 'Fermer' : 'Modifier'}
        </button>
      </div>
      {showHsForm && (
        <div className="flex items-center gap-2 mb-2">
          <input
            type="number" step="0.5" value={hsInput}
            onChange={e => setHsInput(e.target.value)}
            className="form-input text-xs py-1 w-20" placeholder="+/- h"
          />
          <button onClick={() => {
            if (hsInput) { onHeuresSupp(employe.id, parseFloat(hsInput)); setHsInput(''); setShowHsForm(false); }
          }} className="text-xs bg-navy-500 text-white px-2 py-1 rounded hover:bg-navy-800">
            OK
          </button>
        </div>
      )}

      {employe.code_acces && (
        <div className="bg-slate-50 rounded-lg px-3 py-1.5 flex items-center justify-between">
          <span className="text-xs text-slate-400">Code accès :</span>
          <span className="text-xs font-mono font-bold text-slate-600">{employe.code_acces}</span>
        </div>
      )}
      {employe.telephone && (
        <div className="mt-2 text-xs text-gray-500">{employe.telephone}</div>
      )}
      {employe.email && (
        <div className="text-xs text-gray-500 truncate">{employe.email}</div>
      )}
    </div>
  );
}

export default function RHPage() {
  const { getApiParams, structures } = useApp();
  const { user, canSeeFinancials } = useAuth();
  const rhTabsAllowed = allowedRhTabs(user?.role); // ['planning'] pour Équipe, sinon null
  const location = useLocation();
  const tabFromUrl = () => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && (!rhTabsAllowed || rhTabsAllowed.includes(t))) return t;
    return null;
  };
  const [tab, setTab] = useState(tabFromUrl() || (rhTabsAllowed ? rhTabsAllowed[0] : 'employes'));
  // Ouvre l'onglet demandé par le menu de gauche (/rh?tab=xxx)
  useEffect(() => { const t = tabFromUrl(); if (t) setTab(t); /* eslint-disable-next-line */ }, [location.search]);
  const [employes, setEmployes] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmployeForm, setShowEmployeForm] = useState(false);
  const [showCongesForm, setShowCongesForm] = useState(false);
  const [editingEmploye, setEditingEmploye] = useState(null);
  const [filterStructure, setFilterStructure] = useState('');
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7)); // 'YYYY-MM'
  const [releve, setReleve] = useState(null);

  const loadReleve = useCallback(() => {
    const params = { ...getApiParams(), mois };
    employesApi.getReleveMensuel(params).then(res => setReleve(res.data)).catch(() => setReleve(null));
  }, [getApiParams, mois]);

  const loadEmployes = useCallback(() => {
    const params = getApiParams();
    if (filterStructure) params.structure_id = filterStructure;
    employesApi.getAll(params)
      .then(res => setEmployes(res.data))
      .catch(console.error);
  }, [getApiParams, filterStructure]);

  const loadAbsences = useCallback(() => {
    const params = getApiParams();
    employesApi.getAllAbsences(params)
      .then(res => setAbsences(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams]);

  useEffect(() => {
    setLoading(true);
    loadEmployes();
    loadAbsences();
  }, [loadEmployes, loadAbsences]);

  useEffect(() => { if (tab === 'releve') loadReleve(); }, [tab, loadReleve]);

  const handleEditEmploye = (employe) => {
    setEditingEmploye(employe);
    setShowEmployeForm(true);
  };

  const handleTransfertEmploye = async (employe, structureId) => {
    if (!structureId || String(structureId) === String(employe.structure_id)) return;
    const dest = structures.find(s => String(s.id) === String(structureId));
    if (!window.confirm(`Transférer ${employe.prenom} ${employe.nom} vers ${dest?.nom || 'cette structure'} ?`)) return;
    try { await employesApi.transfert(employe.id, structureId); loadEmployes(); }
    catch (e) { window.alert(e.response?.data?.error || 'Transfert impossible'); }
  };

  const handleDeleteEmploye = async (employe) => {
    if (!window.confirm(`Supprimer définitivement ${employe.prenom} ${employe.nom} ?\nCette action est irréversible.`)) return;
    try { await employesApi.delete(employe.id); loadEmployes(); }
    catch (e) { window.alert(e.response?.data?.error || 'Suppression impossible (réservée à l\'administrateur).'); }
  };

  const handleAbsenceStatut = async (id, statut) => {
    try {
      await employesApi.updateAbsenceStatut(id, statut);
      loadAbsences();
      loadEmployes();
    } catch (err) {
      alert('Erreur');
    }
  };

  const handleHeuresSupp = async (employeId, heures) => {
    try {
      await employesApi.updateHeuresSupp(employeId, heures);
      loadEmployes();
    } catch (err) {
      alert('Erreur');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  const absenceTypeLabel = {
    cp: 'CP', cp_anciennete: 'CP Anc.', recup: 'Récup', rtt: 'RTT',
    maladie: 'Maladie', formation: 'Formation', autre: 'Autre',
  };
  const absenceTypeColor = {
    cp: 'badge-green', cp_anciennete: 'badge-orange', recup: 'badge-blue', rtt: 'badge-blue',
    maladie: 'badge-red', formation: 'badge-orange', autre: 'badge-gray',
  };

  const ALL_TABS = [
    { key: 'employes', icon: Users, label: `Employés (${employes.length})` },
    { key: 'conges', icon: Calendar, label: `Congés & Absences (${absences.length})` },
    { key: 'planning', icon: CalendarDays, label: 'Planning' },
    { key: 'releve', icon: FileSpreadsheet, label: 'Relevé mensuel' },
    { key: 'recrutement', icon: UserPlus, label: 'Recrutement' },
    { key: 'refacturation', icon: ArrowLeftRight, label: 'Refacturation' },
  ];
  // Équipe : seulement Planning. Direction (sans financier) : pas de Refacturation.
  const TABS = ALL_TABS.filter(t => {
    if (rhTabsAllowed) return rhTabsAllowed.includes(t.key);
    if (!canSeeFinancials && t.key === 'refacturation') return false;
    return true;
  });

  const shiftMois = (delta) => setMois(prev => {
    const [yy, mm] = prev.split('-').map(Number);
    const d = new Date(yy, mm - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const moisLabel = (() => { const [yy, mm] = mois.split('-').map(Number); return format(new Date(yy, mm - 1, 1), 'MMMM yyyy', { locale: fr }); })();
  const totPaniers = releve ? releve.rows.reduce((s, r) => s + r.paniers_repas, 0) : 0;

  return (
    <div className="space-y-4">
      {/* Modals */}
      {showEmployeForm && (
        <EmployeForm
          employe={editingEmploye}
          structures={structures}
          onClose={() => { setShowEmployeForm(false); setEditingEmploye(null); }}
          onSaved={() => { setShowEmployeForm(false); setEditingEmploye(null); loadEmployes(); }}
        />
      )}
      {showCongesForm && (
        <CongesForm
          employes={employes}
          onClose={() => setShowCongesForm(false)}
          onSaved={() => { setShowCongesForm(false); loadAbsences(); loadEmployes(); }}
        />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><t.icon size={15} /> {t.label}</span>
          </button>
        ))}
      </div>

      {/* ═══ TAB EMPLOYÉS ═══ */}
      {tab === 'employes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <select
              value={filterStructure}
              onChange={e => setFilterStructure(e.target.value)}
              className="form-select w-52 text-sm"
            >
              <option value="">Toutes les structures</option>
              {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <a
                href={employesApi.getExportEmployesCsvUrl(filterStructure ? { structure_id: filterStructure } : {})}
                className="btn-secondary text-xs"
                download
              >
                <Download size={14} /> Export CSV
              </a>
              <button onClick={() => { setEditingEmploye(null); setShowEmployeForm(true); }} className="btn-primary">
                <Plus size={16} /> Nouvel employé
              </button>
            </div>
          </div>
          {loading ? (
            <div className="py-16 text-center text-gray-400">Chargement...</div>
          ) : employes.length === 0 ? (
            <div className="py-16 text-center">
              <Users size={40} className="text-gray-300 mx-auto mb-3" />
              <div className="text-gray-400 text-sm">Aucun employé</div>
              <button onClick={() => setShowEmployeForm(true)} className="btn-primary mt-4">
                <Plus size={16} /> Ajouter un employé
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employes.map(e => (
                <EmployeCard
                  key={e.id}
                  employe={e}
                  onEdit={handleEditEmploye}
                  onHeuresSupp={handleHeuresSupp}
                  structures={structures}
                  canTransfer={['admin', 'direction'].includes(user?.role)}
                  canDelete={user?.role === 'admin'}
                  onTransfer={handleTransfertEmploye}
                  onDelete={handleDeleteEmploye}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB CONGÉS ═══ */}
      {tab === 'conges' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Légende congés ancienneté */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs text-amber-700 flex items-center gap-1.5">
                <Award size={12} />
                CP ancienneté : +5 jours après 2 ans
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={employesApi.getExportAbsencesCsvUrl({})}
                className="btn-secondary text-xs"
                download
              >
                <Download size={14} /> Export CSV
              </a>
              <button onClick={() => setShowCongesForm(true)} className="btn-primary">
                <Plus size={16} /> Nouvelle absence
              </button>
            </div>
          </div>
          <div className="card overflow-hidden">
            {absences.length === 0 ? (
              <div className="py-16 text-center">
                <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
                <div className="text-gray-400 text-sm">Aucune absence enregistrée</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Employé</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Début</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Fin</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Jours</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((a, i) => (
                      <tr key={a.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {a.employe_prenom} {a.employe_nom}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{a.structure_nom}</td>
                        <td className="px-4 py-3">
                          <span className={absenceTypeColor[a.type] || 'badge-gray'}>
                            {absenceTypeLabel[a.type] || a.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(a.date_debut)}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(a.date_fin)}</td>
                        <td className="px-4 py-3 text-gray-700 font-medium">{a.jours}j</td>
                        <td className="px-4 py-3">
                          <span className={getStatutBadgeClass(a.statut)}>{getStatutLabel(a.statut)}</span>
                        </td>
                        <td className="px-4 py-3">
                          {((a.statut === 'en_attente' && (user?.valideur_conges || user?.role === 'admin')) ||
                            (a.statut === 'valide_n1' && user?.role === 'admin')) && (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleAbsenceStatut(a.id, 'approuve')}
                                className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-500"
                                title={a.statut === 'valide_n1' ? 'Validation finale (direction)' : (user?.role === 'admin' ? 'Valider' : 'Valider (1er niveau)')}
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                onClick={() => handleAbsenceStatut(a.id, 'refuse')}
                                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                title="Refuser"
                              >
                                <XCircle size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB PLANNING ═══ */}
      {tab === 'planning' && (
        <PlanningPage />
      )}

      {/* ═══ TAB RELEVÉ MENSUEL ═══ */}
      {tab === 'releve' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-navy-900">Relevé mensuel — paie</h2>
              <p className="text-[13px] text-gray-500">CP &amp; récup validés par salarié + paniers repas (jours travaillés ≥ 6h){getApiParams().structure_id ? '' : ' — toutes structures'}.</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => shiftMois(-1)} className="btn-secondary p-2"><ChevronLeft size={16} /></button>
              <span className="text-[13.5px] text-navy-700 px-2 min-w-[140px] text-center capitalize font-medium">{moisLabel}</span>
              <button onClick={() => shiftMois(1)} className="btn-secondary p-2"><ChevronRight size={16} /></button>
              {releve && (
                <a href={employesApi.getExportAbsencesCsvUrl(getApiParams())} className="btn-secondary text-[12px] ml-1 flex items-center gap-1"><Download size={13} /> Absences CSV</a>
              )}
            </div>
          </div>

          {!releve ? (
            <div className="text-center py-10 text-gray-400 text-sm">Chargement…</div>
          ) : releve.rows.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">Aucun salarié pour cette structure.</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3">
                <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gold-50 text-gold-600 flex items-center justify-center"><UtensilsCrossed size={18} /></div>
                  <div><div className="text-2xl font-semibold text-navy-900 leading-none">{totPaniers}</div><div className="text-[12px] text-gray-500 mt-0.5">Paniers repas à déclarer</div></div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-slate-100">
                      <th className="px-4 py-2.5 font-semibold">Salarié</th>
                      {!getApiParams().structure_id && <th className="px-4 py-2.5 font-semibold">Structure</th>}
                      <th className="px-4 py-2.5 font-semibold text-center">CP pris (j)</th>
                      <th className="px-4 py-2.5 font-semibold text-center">Récup (j)</th>
                      <th className="px-4 py-2.5 font-semibold text-center">Jours travaillés</th>
                      <th className="px-4 py-2.5 font-semibold text-center">Paniers repas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releve.rows.map(r => (
                      <tr key={r.employe_id} className="border-b border-slate-50 hover:bg-cream-50/50">
                        <td className="px-4 py-2.5 font-medium text-navy-900">{r.prenom} {r.nom}{r.poste ? <span className="text-gray-400 font-normal text-[12px]"> · {r.poste}</span> : null}</td>
                        {!getApiParams().structure_id && <td className="px-4 py-2.5 text-gray-500 text-[12.5px]">{r.structure_nom || '—'}</td>}
                        <td className="px-4 py-2.5 text-center">{r.cp_jours || '—'}</td>
                        <td className="px-4 py-2.5 text-center">{r.recup_jours || '—'}</td>
                        <td className="px-4 py-2.5 text-center text-gray-500">{r.jours_travailles || '—'}</td>
                        <td className="px-4 py-2.5 text-center font-semibold text-gold-700">{r.paniers_repas || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11.5px] text-gray-400">Paniers repas = nombre de jours du mois où le salarié est planifié au moins 6h. CP &amp; récup comptabilisés uniquement s'ils sont <strong>validés</strong> (onglet Congés &amp; Absences).</p>
            </>
          )}
        </div>
      )}

      {/* ═══ TAB REFACTURATION ═══ */}
      {tab === 'recrutement' && (
        <RecrutementPage structures={structures} />
      )}

      {tab === 'refacturation' && (
        <RefacturationPage />
      )}
    </div>
  );
}
